import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { queryKeysDeliveryNote } from '../queryKeys';

import { bulkBillDlnItems } from './bulkBillDlnItems';
import {
  type BulkBillDlnItemsMutationArguments,
  type BulkBillDlnItemsResponse,
} from './types';

/**
 * React Query mutation hook for bulk-billing delivery note items.
 *
 * @param options - Optional TanStack Query mutation options for customizing behavior.
 * @returns A mutation object with methods to trigger the bulk billing.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/bulk_bill_dln_items_delivery_note__asset_id__item_bulk_bill_put
 */
export const useMutationBulkBillDlnItems = (
  options?: Omit<
    UseMutationOptions<
      BulkBillDlnItemsResponse,
      Error,
      BulkBillDlnItemsMutationArguments
    >,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  const { onSettled, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: ({ assetId, payload }: BulkBillDlnItemsMutationArguments) =>
      bulkBillDlnItems(assetId, payload),
    onSettled: (data, error, variables, onMutateResult, context) => {
      if (variables?.assetId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeysDeliveryNote.getInternal(variables.assetId),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeysDeliveryNote.get(variables.assetId),
        });
      }

      void onSettled?.(data, error, variables, onMutateResult, context);
    },
    ...restOptions,
  });
};
